import { ButtonType, DatePickerRange, Form, CustomButton, CustomSubmitButton, CustomSubmitButtonType } from "soprasteria-ui-library";
import { SortOrder } from "soprasteria-ui-library/dist/models/IPagination";
import { faFileExcel, faDownload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { ListingsApi } from "../../../api/ListingsApi";
import { useAuth } from "../../../App";
import { isInRole, UserContext } from "../../../contexts/UserContext";
import { ICareProvider } from "../../../models/ICareProvider";
import { IListingSearchParams } from "../../../models/IPatientsListings";
import { LIBRETTOADMIN, VÅRDANSVARIG } from "../../../utils/RoleConstants";
import { CareProviderDropdown } from "../../CareProviderDropdown/CareProviderDropdown";
import { CustomDatePickerRange } from "../../../custom/CustomDatePickerRange";

interface IPatientListingSearch {
    onSearchPatientListings: (params: IListingSearchParams) => void;
    selectedSordOrderBy: string;
    selectedSortOrder: SortOrder;
    preSetSearchParams?: IListingSearchParams;
}

export const PatientListingSearch: React.FunctionComponent<IPatientListingSearch> = ({ onSearchPatientListings, selectedSordOrderBy, selectedSortOrder, preSetSearchParams }) => {

    const auth = useAuth();
    const api = new ListingsApi(auth);

    const userInfo = React.useContext(UserContext);
    const hasRoleParaply = isInRole(VÅRDANSVARIG, userInfo.roles);
    const isSysAdmin = isInRole(LIBRETTOADMIN, userInfo.roles);

    const [isFetchingCareProviders, setIsFetchingCareProviders] = React.useState<boolean>(false);
    const [isFetchingUmbrellaOrganisations, setIsFetchingUmbrellaOrganisations] = React.useState<boolean>(false);

    const [careProviders, setCareProviders] = React.useState<ICareProvider[]>([]);
    const [umbrellaOrganisations, setUmbrellaOrganisations] = React.useState<ICareProvider[]>([]);

    const [selectedUmbrellaOrganisationId, setSelectedUmbrellaOrganisationId] = React.useState<number>();
    const [resetCareProviderValue, doResetCareProviderValue] = React.useState<boolean>(false);
    const [firstTimeLoaded, setFirstTimeLoaded] = React.useState<boolean>(true);

    const [isExportingToExcel, setIsExportingToExcel] = React.useState<boolean>(false);
    const [isExportingToTxt, setIsExportingToTxt] = React.useState<boolean>(false);
    const [isExportListingsOverview, setIsExportListingsOverview] = React.useState<boolean>(false);
    const [isExportRevisionEndOverview, setIsExportRevisionEndOverview] = React.useState<boolean>(false);

    const [selectedRevisionEndDateFrom, setSelectedRevisionEndDateFrom] = React.useState<Date | null | undefined>(preSetSearchParams?.revisionEndDateFrom);
    const [selectedRevisionEndDateTo, setSelectedRevisionEndDateTo] = React.useState<Date | null | undefined>(preSetSearchParams?.revisionEndDateTo);

    React.useEffect(() => {
        getUmbrellaOrganisations(preSetSearchParams?.umbrellaCareProviderId);
    }, [])

    const getUmbrellaOrganisations = (umbrellaCareProviderId?: number) => {
        setIsFetchingUmbrellaOrganisations(true);
        api.getUmbrellaOrganisationsWithActiveListings((umbrellaOrganisations) => {
            const umbrellaCareProviderIdToUse = umbrellaCareProviderId ?? umbrellaOrganisations[0].careProviderId;
            setSelectedUmbrellaOrganisationId(umbrellaCareProviderIdToUse)
            setUmbrellaOrganisations(umbrellaOrganisations);
            getUmbrellaCareProvidersByUmbrellaCareProviderId(umbrellaCareProviderIdToUse, umbrellaOrganisations);
        }, undefined, () => {
            setIsFetchingUmbrellaOrganisations(false);
        });
    }

    const getUmbrellaCareProvidersByUmbrellaCareProviderId = (umbrellaCareProviderId: number, umbrellaOrganisations: ICareProvider[]) => {
        setIsFetchingCareProviders(true);
        api.getUmbrellaCareProvidersByUmbrellaCareProviderId(umbrellaCareProviderId, (careProviders) => {
            if (careProviders.length !== 0) {
                setCareProviders(careProviders);
            } else {
                setCareProviders([]);
            }

            if (firstTimeLoaded) {
                document.getElementById('submitbutton')?.click();
                setFirstTimeLoaded(false);
            }
        }, undefined, () => {
            setIsFetchingCareProviders(false);
        });
    }

    const onUmbrellaCareProviderChange = (selectedCareProvider: string | undefined) => {
        if (isSysAdmin) {
            if (selectedCareProvider && selectedCareProvider !== "") {
                getUmbrellaCareProvidersByUmbrellaCareProviderId(parseInt(selectedCareProvider), umbrellaOrganisations);
                setSelectedUmbrellaOrganisationId(parseInt(selectedCareProvider));
            }
            else {
                setSelectedUmbrellaOrganisationId(undefined);
                setCareProviders([]);
            }
            doResetCareProviderValue(prev => !prev);
        }
    }

    const onSearchFormToExcelSubmit = (searchParameters: IListingSearchParams) => {
        setIsExportingToExcel(true);
        var currentSearchParams = searchParameters;
        currentSearchParams.pageNumber = 1;
        currentSearchParams.propertyToOrderBy = selectedSordOrderBy;
        currentSearchParams.sortOrder = selectedSortOrder;
        api.getListedPatientsAsExcel(currentSearchParams,
            "Listade patienter",
            () => { },
            () => { },
            () => {
                setIsExportingToExcel(false);
            }
        );
    }

    const exportToTxt = () => {
        setIsExportingToTxt(true);
        api.getListedUnlistedPatientsAsTxt(
            () => { },
            () => { },
            () => setIsExportingToTxt(false)
        );
    }

    const getUmbrellaListingsExcelReport = () => {
        setIsExportListingsOverview(true);
        api.getUmbrellaListingsExcelReport(
            "Översikt listningar", umbrellaOrganisations[0].careProviderId,
            () => { },
            () => { },
            () => {
                setIsExportListingsOverview(false);
            }
        );
    }

    const getUmbrellaRevisionEndExcelReport = () => {
        var endDateFrom: Date | undefined = selectedRevisionEndDateFrom ? new Date(selectedRevisionEndDateFrom) : undefined;
        var endDateTo: Date | undefined = selectedRevisionEndDateTo ? new Date(selectedRevisionEndDateTo) : undefined;
        var searchParameters: IListingSearchParams = {
            careProviderId: umbrellaOrganisations[0].careProviderId,
            revisionEndDateFrom: endDateFrom,
            revisionEndDateTo: endDateTo,
            umbrellaCareProviderId: umbrellaOrganisations[0].careProviderId,
            pageNumber: 1,
            pageSize: 10
        };
        setIsExportRevisionEndOverview(true);
        api.getUmbrellaRevisionEndExcelReport(searchParameters,
            "Översikt revisionsslut",
            () => { },
            () => { },
            () => {
                setIsExportRevisionEndOverview(false);
            }
        );
    }

    const getUmbrellaCareProviderDefaultValue = () => {
        if (selectedUmbrellaOrganisationId) {
            return selectedUmbrellaOrganisationId.toString();
        }

        if (umbrellaOrganisations?.length === 1) {
            return umbrellaOrganisations[0].careProviderId.toString();
        }

        return undefined;
    }

    const getCareProviderDefaultValue = () => {
        if (careProviders && careProviders.length > 0) {
            const preSetSerachParamsCareProviderId = preSetSearchParams?.careProviderId
                ? parseInt(preSetSearchParams.careProviderId.toString())
                : undefined;
            const isPreSetCareProviderIdInCareProviders = careProviders.some((careProvider) => careProvider.careProviderId === preSetSerachParamsCareProviderId);

            if (isPreSetCareProviderIdInCareProviders) {
                return preSetSearchParams?.careProviderId?.toString();
            }

            if (careProviders[0] !== undefined) {
                return careProviders[0].careProviderId.toString();
            }
        }

        return undefined;
    }

    const search = (data: IListingSearchParams) => {
        if (data.careProviderId) {
            if (firstTimeLoaded && preSetSearchParams?.pageNumber) {
                data.pageNumber = preSetSearchParams.pageNumber;
                if (preSetSearchParams.sortOrder) {
                    data.sortOrder = preSetSearchParams.sortOrder;
                }
                if (preSetSearchParams.propertyToOrderBy) {
                    data.propertyToOrderBy = preSetSearchParams.propertyToOrderBy ?? undefined;
                }
            }
            onSearchPatientListings(data);
        }
    }

    const onChangeRevisionEndDateFrom = (date: Date | null) => {
        setSelectedRevisionEndDateFrom(date);
    }

    const onChangeRevisionEndDateTo = (date: Date | null) => {
        setSelectedRevisionEndDateTo(date);
    }

    return (
        <section>
            <Form onSubmit={search}>
                <div className="row">
                    <div className="col-6">
                        <CareProviderDropdown
                            name="umbrellaCareProviderId"
                            label="Vårdgivare"
                            isLoading={isFetchingUmbrellaOrganisations}
                            careProviders={umbrellaOrganisations}
                            onValueChange={onUmbrellaCareProviderChange}
                            defaultValue={getUmbrellaCareProviderDefaultValue()}
                            disabled={!isSysAdmin}
                            useFixedListItemHeight
                            showOrgNum
                            placeholder="Välj vårdgivare"
                            isClearable
                            required
                        />
                    </div>
                    <div className="col-6" />
                    <div className="col-6">
                        <CareProviderDropdown
                            name="careProviderId"
                            label="Mottagning"
                            isLoading={isFetchingCareProviders}
                            careProviders={careProviders ?? []}
                            defaultValue={getCareProviderDefaultValue()}
                            disabled={!(hasRoleParaply || isSysAdmin)}
                            showClinicId
                            isClearable
                            required
                            resetValue={resetCareProviderValue}
                        />
                        <CustomDatePickerRange name="revisionEndDateFrom" nameSecondary="revisionEndDateTo" label="Revisionsslut" inlineLabel
                            value={selectedRevisionEndDateFrom ? new Date(selectedRevisionEndDateFrom) : undefined}
                            valueSecondary={selectedRevisionEndDateTo ? new Date(selectedRevisionEndDateTo) : undefined}
                            onChangeFrom={onChangeRevisionEndDateFrom} onChangeTo={onChangeRevisionEndDateTo}
                        />
                    </div>
                    <div className="col-12">
                        <div className="text-right panel">
                            <span className="d-none">
                                <CustomSubmitButton onButtonSubmit={search}>sök</CustomSubmitButton>
                            </span>
                            {
                                hasRoleParaply ?
                                    <CustomButton buttonType={ButtonType.download}
                                        buttonText=" Exportera översikt revisionsslut"
                                        onClick={getUmbrellaRevisionEndExcelReport}
                                        isLoading={isExportRevisionEndOverview}
                                        disabled={selectedRevisionEndDateFrom == null || selectedRevisionEndDateTo == null}>
                                        <FontAwesomeIcon icon={faDownload} />
                                    </CustomButton>
                                    : null

                            }
                            {
                                hasRoleParaply ?
                                    <CustomButton buttonType={ButtonType.download} buttonText=" Exportera översikt listade patienter" onClick={getUmbrellaListingsExcelReport} isLoading={isExportListingsOverview} />
                                    : null

                            }
                            {
                                !hasRoleParaply && !isSysAdmin ?
                                    <CustomButton buttonType={ButtonType.download} buttonText=" Exportera listade/olistade patienter" onClick={exportToTxt} isLoading={isExportingToTxt} />
                                    : null

                            }
                            <CustomSubmitButton buttonType={CustomSubmitButtonType.alternative} isLoading={isExportingToExcel} onButtonSubmit={onSearchFormToExcelSubmit}><FontAwesomeIcon icon={faFileExcel} /> Exportera till Excel</CustomSubmitButton>
                            <CustomSubmitButton id="submitbutton">Sök</CustomSubmitButton>
                        </div>
                    </div>
                </div>
            </Form>
        </section>
    );
}